import { createContext, useContext, useState, ReactNode } from 'react';

export interface AppointmentData {
  id: string;
  petId: string;
  petName: string;
  petSpecies: 'dog' | 'cat';
  professionalType: string;
  professionalName: string;
  professionalContact?: string;
  service: string;
  date: Date;
  duration: number; // in minutes
  status:
    | 'scheduled'
    | 'confirmed'
    | 'completed'
    | 'cancelled'
    | 'pending-payment';
  location: string;
  notes?: string;
  paymentStatus: 'paid' | 'pending' | 'refunded';
  createdAt: Date;
}

interface AppointmentsContextType {
  appointments: AppointmentData[];
  addAppointment: (appointment: AppointmentData) => void;
  updateAppointment: (id: string, updates: Partial<AppointmentData>) => void;
  cancelAppointment: (id: string) => void;
  getAppointmentsByPet: (petId: string) => AppointmentData[];
  getAllAppointments: () => AppointmentData[];
  getUpcomingAppointments: () => AppointmentData[];
}

const AppointmentsContext = createContext<AppointmentsContextType | undefined>(
  undefined
);

export function AppointmentsProvider({ children }: { children: ReactNode }) {
  const [appointments, setAppointments] = useState<AppointmentData[]>([]);

  const addAppointment = (appointment: AppointmentData) => {
    setAppointments((prev) => [...prev, appointment]);
  };

  const updateAppointment = (id: string, updates: Partial<AppointmentData>) => {
    setAppointments((prev) =>
      prev.map((apt) => (apt.id === id ? { ...apt, ...updates } : apt))
    );
  };

  const cancelAppointment = (id: string) => {
    setAppointments((prev) =>
      prev.map((apt) =>
        apt.id === id ? { ...apt, status: 'cancelled' as const } : apt
      )
    );
  };

  const getAppointmentsByPet = (petId: string) => {
    return appointments.filter((apt) => apt.petId === petId);
  };

  const getAllAppointments = () => {
    return appointments;
  };

  const getUpcomingAppointments = () => {
    const now = new Date();
    return appointments
      .filter(
        (apt) =>
          apt.date >= now &&
          apt.status !== 'cancelled' &&
          apt.status !== 'completed'
      )
      .sort((a, b) => a.date.getTime() - b.date.getTime());
  };

  return (
    <AppointmentsContext.Provider
      value={{
        appointments,
        addAppointment,
        updateAppointment,
        cancelAppointment,
        getAppointmentsByPet,
        getAllAppointments,
        getUpcomingAppointments,
      }}
    >
      {children}
    </AppointmentsContext.Provider>
  );
}

export function useAppointments() {
  const context = useContext(AppointmentsContext);
  if (!context) {
    throw new Error('useAppointments must be used within AppointmentsProvider');
  }
  return context;
}
